import type { MutableRefObject } from 'react';
import { useCallback, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { useSelector } from 'react-redux';
import { selectSections } from '../grid/reduxStore/editorSlice';
import { reorderItems } from './reorderUtils';
import { SectionGhost } from './SectionGhost';
import type { ActiveDraggedSectionInfo, OnReorderFn } from './types';

type DropPosition = 'top' | 'bottom' | null;
export type UseSectionDragAndDropProps = {
  sectionId: string;
  index: number;
  title: string;
  onReorder: OnReorderFn;
  draggedRef: MutableRefObject<ActiveDraggedSectionInfo | null>;
};

const addPreview = (event: React.DragEvent, title: string) => {
  // create an invisible container to create the React element as a DOM element
  // and then use that DOM element as image for the drag image
  const ghostContainer = document.createElement('div');
  ghostContainer.style.position = 'absolute';
  ghostContainer.style.top = '-9999px';
  ghostContainer.style.left = '-9999px';
  ghostContainer.id = 'temporary-drag-preview';

  document.body.appendChild(ghostContainer);

  createRoot(ghostContainer).render(<SectionGhost title={title} />);

  event.dataTransfer.setDragImage(ghostContainer, 10, 10);
};

const removePreview = () => {
  document.getElementById('temporary-drag-preview')?.remove();
};

export const useSectionDragAndDrop = ({ sectionId, index, title, onReorder, draggedRef }: UseSectionDragAndDropProps) => {
  const sections = useSelector(selectSections);
  const [dropPosition, setDropPosition] = useState<DropPosition | null>(null);
  const sectionRef = useRef<HTMLLIElement>(null);

  const canDrop = useCallback(
    (dropPosition?: DropPosition) => {
      if (!draggedRef.current || draggedRef.current.sectionId === sectionId) return false;
      if (!dropPosition) return true;

      if (dropPosition === 'top') {
        return sections[index - 1]?.id !== draggedRef.current?.sectionId;
      } else if (dropPosition === 'bottom') {
        return sections[index + 1]?.id !== draggedRef.current?.sectionId;
      }
      return false;
    },
    [sections, index]
  );

  const handleDragStart = (event: React.DragEvent) => {
    addPreview(event, title);
    event.dataTransfer.setData('text/plain', JSON.stringify({ sectionId, index }));
    event.dataTransfer.effectAllowed = 'move';
    draggedRef.current = { sectionId, index, title };
  };

  const handleDragOver = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      if (!canDrop()) {
        setDropPosition(null);
        return;
      }

      if (sectionRef.current) {
        const elementRect = sectionRef.current.getBoundingClientRect();
        const elementRectRelativeY = event.clientY - elementRect.top;
        // Add hysteresis to prevent flickering when near the middle
        let isTopHalf;
        if (dropPosition === 'top') {
          // Stay top until cursor moves below 75%
          isTopHalf = elementRectRelativeY < elementRect.height * 0.75;
        } else if (dropPosition === 'bottom') {
          // Stay bottom until cursor moves above 25%
          isTopHalf = elementRectRelativeY < elementRect.height * 0.25;
        } else {
          // Initial case - use 50% threshold
          isTopHalf = elementRectRelativeY < elementRect.height * 0.5;
        }

        if (isTopHalf && canDrop('top')) {
          setDropPosition('top');
        } else if (canDrop('bottom')) {
          setDropPosition('bottom');
        }
      }
    },
    [dropPosition]
  );

  const handleDragLeave = (event: React.DragEvent) => {
    event.preventDefault();
    setDropPosition(null);
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    const draggedItem = draggedRef.current;
    if (!draggedItem || !dropPosition) {
      return;
    }

    const toIndex = dropPosition === 'bottom' ? index + 0.5 : index - 0.5;
    const newSequence = reorderItems(sections, draggedItem.index, toIndex);
    onReorder(newSequence);
    setDropPosition(null);
    removePreview();
  };

  const handleDragEnd = (event: React.DragEvent) => {
    event.preventDefault();
    setDropPosition(null);
    draggedRef.current = null;
  };

  return {
    sectionRef,
    dropPosition,
    dragHandlers: {
      draggable: true,
      onDragStart: handleDragStart,
      onDragOver: handleDragOver,
      onDragLeave: handleDragLeave,
      onDrop: handleDrop,
      onDragEnd: handleDragEnd,
    },
  };
};
